/**
 * Render-time identity for what ContentBodyLibraries has loaded and which view it shows.
 *
 * Pure so it is unit-testable without the component's redux/auth chain. Both decisions
 * are made DURING RENDER on purpose: an effect-driven version leaves one committed
 * render in which the previous directory's content (or view) is paired with the new
 * directory, and children mounted in that render start fetching before any effect can
 * correct it.
 */

export interface IDirectoryContentIdentity {
    siteId?: string;
    tessAccountId?: string;
    tessProjectId?: string;
}

/**
 * Identity of the content set a directory loads. Plain sites reduce to their siteId;
 * Tess directories inherit siteId from the nearest ancestor, so the account and
 * project ids are folded in to keep two Tess directories under one site apart. The
 * same shape the aggregate view is remounted on.
 */
export const getDirectoryContentKey = (directory: IDirectoryContentIdentity | null | undefined): string => {
    if (!directory) {
        return "";
    }
    return `${directory.siteId ?? ""}|${directory.tessAccountId ?? ""}|${directory.tessProjectId ?? ""}`;
};

export interface IAllDocumentsViewOverride {
    scopeId: string | undefined;
    value: boolean;
}

/**
 * Whether the aggregate "All documents" view is what a scope shows. A toggle made in
 * this session wins for the scope it was made in; anywhere else the persisted
 * preference decides. No scope means the view is unavailable.
 */
export const resolveAllDocumentsView = (
    scopeId: string | undefined,
    persistedPreference: boolean,
    override: IAllDocumentsViewOverride | null | undefined
): boolean => {
    if (!scopeId) {
        return false;
    }
    if (override && override.scopeId === scopeId) {
        return override.value;
    }
    return persistedPreference === true;
};
